import React, { useState, useEffect } from "react";
import DashboardNavbar from "./DashboardNavbar";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useLoading } from "./LoadingContext";
import Pagination from "../Pagination";

function AllData() {
  const [data, setData] = useState([]);
  const { loading, setLoading } = useLoading();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [validation, setValidation] = useState("");
  const [jobDescriptionIds, setJobDescriptionIds] = useState([]); // Holds the list of job description IDs
  const [jobDescriptionId, setJobDescriptionId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [minExperience, setMinExperience] = useState("");
  const [maxExperience, setMaxExperience] = useState("");
  const [skillsFilter, setSkillsFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [candidateCurrentStatuses, setCandidateCurrentStatuses] = useState([]);
  const [candidateCurrentStatus, setCandidateCurrentStatus] = useState("");
  const [candidateCurrentStatusestb, setCandidateCurrentStatusestb] = useState(
    []
  );
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [attachedJDFile, setAttachedJDFile] = useState(null);
  const [setError] = useState(null);
  const location = useLocation(); // Access current location (URL)

  const searchParams = new URLSearchParams(location.search); // Create URLSearchParams object
  const candidateStatusQuery = searchParams.get("metric_key"); // Extract candidate_status from the URL

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1); // Reset to first page whenever filters change
  }, [jobDescriptionId, validation, candidateCurrentStatus, fromDate, toDate]);

  // Function to fetch candidate statusesfor table dropdown from the API
  const fetchCandidateStatusestb = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/candidate/recruitment-statuses/`
      );
      const statuses = response.data.data;
      setCandidateCurrentStatusestb(statuses);
    } catch (err) {
      console.error("Error fetching candidate statuses:", err);
    }
  };

  // Use useEffect to fetch the statuses when the component mounts
  useEffect(() => {
    fetchCandidateStatusestb();
  }, []);

  // Fetch data function
  const fetchData = async (url) => {
    setLoading(true);
    try {
      const response = await fetch(url);
      const result = await response.json();
      if (response.ok) {
        setData(result);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  // Use Effect to handle data fetching based on candidate_status query
  useEffect(() => {
    const storedClient = localStorage.getItem("client");
    const storedUser = localStorage.getItem("user_id");

    // Convert stored values to numbers
    const client_id = storedClient ? Number(storedClient) : null;
    const user_id = storedUser ? Number(storedUser) : null;

    // Debugging: Log retrieved values
    console.log("Fetching data with:", { client_id, user_id });

    // Ensure client_id and user_id are valid before making the request
    if (!client_id || !user_id) {
      console.error("Missing client_id or user_id. Please log in again.");
      return;
    }
    let url = `${process.env.REACT_APP_API_BASE_URL}/screener/resumes-candidates?client_id=${client_id}&user_id=${user_id}`;
    if (candidateStatusQuery) {
      url += `&metric_key=${candidateStatusQuery}`;
    }
    fetchData(url);
  }, [candidateStatusQuery]);

  // Handle button click to show all data
  const handleShowAllData = () => {
    const storedClient = localStorage.getItem("client");
    const storedUser = localStorage.getItem("user_id");

    // Convert stored values to numbers
    const client_id = storedClient ? Number(storedClient) : null;
    const user_id = storedUser ? Number(storedUser) : null;

    // Debugging: Log retrieved values
    console.log("Fetching data with:", { client_id, user_id });

    // Ensure client_id and user_id are valid before making the request
    if (!client_id || !user_id) {
      console.error("Missing client_id or user_id. Please log in again.");
      return;
    }
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete("candidate_status");
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
    fetchData(
      `${process.env.REACT_APP_API_BASE_URL}/screener/resumes-candidates?client_id=${client_id}&user_id=${user_id}`
    );
  };

  // Function for fetching JD IDs
  useEffect(() => {
    const fetchData = async () => {
      const storedClient = localStorage.getItem("client");
      const storedUser = localStorage.getItem("user_id");

      // Convert stored values to numbers
      const client_id = storedClient ? Number(storedClient) : null;
      const user_id = storedUser ? Number(storedUser) : null;

      // Debugging: Log retrieved values
      console.log("Fetching data with:", { client_id, user_id });

      // Ensure client_id and user_id are valid before making the request
      if (!client_id || !user_id) {
        console.error("Missing client_id or user_id. Please log in again.");
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/screener/resumes-candidates?client_id=${client_id}&user_id=${user_id}`
        );
        const result = await response.json();
        // console.log("API Response: ", result);
        if (response.ok) {
          // Extract job_description_id and remove duplicates using Set
          const jobIds = [
            ...new Set(result.map((item) => item.job_description_id)),
          ];
          setJobDescriptionIds(jobIds); // Store the unique IDs in state
        } else {
          setError("Failed to fetch data");
        }
      } catch (err) {
        setError("Error fetching data");
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCandidateStatuses = async () => {
      const storedClient = localStorage.getItem("client");
      const storedUser = localStorage.getItem("user_id");

      // Convert stored values to numbers
      const client_id = storedClient ? Number(storedClient) : null;
      const user_id = storedUser ? Number(storedUser) : null;

      // Debugging: Log retrieved values
      console.log("Fetching data with:", { client_id, user_id });

      // Ensure client_id and user_id are valid before making the request
      if (!client_id || !user_id) {
        console.error("Missing client_id or user_id. Please log in again.");
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/screener/resumes-candidates?client_id=${client_id}&user_id=${user_id}`
        );
        const result = await response.json();
        if (response.ok) {
          // Assuming the candidate statuses are part of the result
          const statuses = [
            ...new Set(
              result
                .map((item) => item.candidate_current_status)
                .filter(Boolean)
            ),
          ];
          setCandidateCurrentStatuses(statuses);
        } else {
          console.error("Failed to fetch candidate statuses");
        }
      } catch (err) {
        console.error("Error fetching candidate statuses", err);
      }
    };

    fetchCandidateStatuses();
  }, []);

  const handleInitiateOnboarding = async (candidateId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/bgv_form/trigger-mail/${candidateId}`,
        {
          candidate_id: candidateId,
        }
      );

      if (response.status === 200) {
        alert("Onboarding initiated successfully!");
      } else {
        alert("Failed to initiate onboarding.");
      }
    } catch (error) {
      console.error("Error initiating onboarding:", error);
      alert("An error occurred while initiating onboarding.");
    }
  };

  // const filteredItems = data.filter((item) => {
  //   const matchesJobDescriptionId = item.job_description_id
  //     .toString()
  //     .includes(jobDescriptionId);
  //   const matchesValidation = item.validation
  //     ? item.validation.toLowerCase().includes(validation.toLowerCase())
  //     : true;

  //   const matchesCandidateCurrentStatus = candidateCurrentStatus
  //     ? item.candidate_current_status &&
  //       item.candidate_current_status.toLowerCase() ===
  //         candidateCurrentStatus.toLowerCase()
  //     : true;

  //   // Convert item date and filter dates to just the YYYY-MM-DD part for comparison
  //   const itemDate = new Date(item.updated_at).toISOString().slice(0, 10);
  //   const formattedFromDate = fromDate
  //     ? new Date(fromDate).toISOString().slice(0, 10)
  //     : null;
  //   const formattedToDate = toDate
  //     ? new Date(toDate).toISOString().slice(0, 10)
  //     : null;

  //   // Date filtering logic
  //   const fromDateMatch = formattedFromDate
  //     ? itemDate >= formattedFromDate
  //     : true;
  //   const toDateMatch = formattedToDate ? itemDate <= formattedToDate : true;

  //   return (
  //     (jobDescriptionId === "" || matchesJobDescriptionId) &&
  //     (validation === "" || matchesValidation) &&
  //     (candidateCurrentStatus === "" || matchesCandidateCurrentStatus) &&
  //     fromDateMatch &&
  //     toDateMatch &&
  //     matchesExperienceRange &&
  //     matchesSkills &&
  //     matchesLocation
  //   );
  // });

  const filteredItems = data.filter((item) => {
    const matchesJobDescriptionId = jobDescriptionId
      ? item.job_description_id?.toString().includes(jobDescriptionId)
      : true;

    const matchesValidation = validation
      ? item.validation?.toLowerCase().includes(validation.toLowerCase())
      : true;

    const matchesCandidateCurrentStatus = candidateCurrentStatus
      ? item.candidate_current_status?.toLowerCase() ===
        candidateCurrentStatus.toLowerCase()
      : true;

    // Date range filtering (only if fromDate or toDate is set)
    const itemDate = new Date(item.updated_at).toISOString().slice(0, 10);
    const formattedFromDate = fromDate
      ? new Date(fromDate).toISOString().slice(0, 10)
      : null;
    const formattedToDate = toDate
      ? new Date(toDate).toISOString().slice(0, 10)
      : null;

    const fromDateMatch = formattedFromDate
      ? itemDate >= formattedFromDate
      : true;
    const toDateMatch = formattedToDate ? itemDate <= formattedToDate : true;

    // ✅ Experience filter (only if min or max is set)
    const matchesExperienceRange = (() => {
      const exp = item.exp_years;
      // ✅ If user hasn't applied any filter, skip experience check
      if (minExperience === "" && maxExperience === "") return true;

      // ❌ If exp_years is missing or not a number, exclude it
      if (typeof exp !== "number" || isNaN(exp)) return false;

      const min = minExperience ? parseFloat(minExperience) : null;
      const max = maxExperience ? parseFloat(maxExperience) : null;

      if (min !== null && exp < min) return false;
      if (max !== null && exp > max) return false;

      return true;
    })();

    const matchesSkills = skillsFilter
      ? item.skills?.toLowerCase().includes(skillsFilter.toLowerCase())
      : true;

    const matchesLocation = locationFilter
      ? item.location?.toLowerCase().includes(locationFilter.toLowerCase()) ||
        item.current_location
          ?.toLowerCase()
          .includes(locationFilter.toLowerCase())
      : true;

    return (
      matchesJobDescriptionId &&
      matchesValidation &&
      matchesCandidateCurrentStatus &&
      fromDateMatch &&
      toDateMatch &&
      matchesExperienceRange &&
      matchesSkills &&
      matchesLocation
    );
  });

  const handleCheckboxChange = (id) => {
    setSelectedCandidateIds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAllPage = () => {
    const pageCandidateIds = paginatedItems.map((item) => item.id);
    const allSelected = pageCandidateIds.every((id) =>
      selectedCandidateIds.includes(id)
    );

    if (allSelected) {
      // Unselect all
      setSelectedCandidateIds((prev) =>
        prev.filter((id) => !pageCandidateIds.includes(id))
      );
    } else {
      // Select all
      const newSelected = Array.from(
        new Set([...selectedCandidateIds, ...pageCandidateIds])
      );
      setSelectedCandidateIds(newSelected);
    }
  };

  const handleSendFilteredResumes = async () => {
    const storedUser = localStorage.getItem("user_id");
    if (!storedUser) {
      alert("User not logged in.");
      return;
    }

    if (selectedCandidateIds.length === 0) {
      alert("Please select at least one profile.");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", Number(storedUser));
    formData.append("candidate_ids", JSON.stringify(selectedCandidateIds));
    formData.append("message", emailMessage);
    if (attachedJDFile) {
      formData.append("jd_file", attachedJDFile);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/screener/send-resumes-mail`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.status === 200) {
        alert("Email sent successfully!");
        setSelectedCandidateIds([]);
        setEmailMessage("");
        setAttachedJDFile(null);
        setShowSendMailModal(false);
      } else {
        alert("Failed to send email.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("An error occurred while sending mail.");
    }
  };

  // Sorting logic
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === "string") {
        return (
          aValue.localeCompare(bValue) *
          (sortConfig.direction === "ascending" ? 1 : -1)
        );
      } else if (typeof aValue === "number") {
        return (
          (aValue - bValue) * (sortConfig.direction === "ascending" ? 1 : -1)
        );
      }
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "▲▼"; // Default arrow for unsorted columns
    return sortConfig.direction === "ascending" ? "▲" : "▼";
  };

  const handleDownload = async () => {
    // Retrieve user_id from localStorage
    const storedUser = localStorage.getItem("user_id");
    const user_id = storedUser ? Number(storedUser) : null;

    // Debugging: Log retrieved values
    console.log("Uploading with:", { user_id });

    if (!user_id) {
      alert("User ID is missing");
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/screener/resumes/details/download-csv?user_id=${user_id}`,
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Screenify_Candidate_Evaluation_Report.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Download failed");
      }
    } catch (error) {
      console.error("An error occurred during download", error);
    }
  };

  // Loading state rendering
  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-dark">&nbsp;Data is Fetching....</h3>
      </div>
    );
  }

  // if (error) return <div className="error">{error}</div>;

  // Function to clear dropdowns
  const handleClearOptions = () => {
    setValidation("");
    setCandidateCurrentStatus("");
    setJobDescriptionId("");
    setFromDate("");
    setToDate("");
    setMinExperience("");
    setMaxExperience("");
    setSkillsFilter("");
    setLocationFilter("");
  };

  const handleChange = async (e, id, field) => {
    const newValue = e.target.value;

    // Prepare the update data based on the field
    const updateData = {
      [field]: newValue,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/candidate/candidates/${id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        }
      );
      console.log(updateData);
      if (response.ok) {
        const result = await response.json();
        alert(result.message || "Update successful!"); // Show success message from backend
        setData((prevData) => {
          const updatedData = prevData.map((item) =>
            item.id === id ? { ...item, [field]: newValue } : item
          );
          return updatedData;
        });
      } else {
        const errorResult = await response.json();
        alert(errorResult.message || "Failed to update data."); // Show error message from backend
      }
    } catch (error) {
      console.error("An error occurred while updating data", error);
      alert("An unexpected error occurred."); // Show generic error message
    }
  };

  const RatingBreakdown = ({ breakdown }) => {
    return (
      <div className="text-start">
        {Object.entries(breakdown || {}).map(([key, value]) => (
          <div key={key}>
            <span>{key.replace(/_/g, " ").toUpperCase()}</span>:{" "}
            <span style={{ color: "#0d0d0d", fontSize: "1.1em" }}>{value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Navbar Code */}
      <DashboardNavbar />
      <div className="container py-3 text-">
        <h3 className="text-center font_header">Candidate Evaluation Report</h3>
        <div className="row align-items-center">
          <div className="col-sm-12 mt-2 mb-4 text-gred">
            <div className="d-flex align-items-start justify-content-between">
              <form className="row g-3 align-items-center flex-grow-1 fw-bold">
                <div className="col-lg-2 col-md-5 mb-1">
                  <label
                    htmlFor="jobDescriptionId"
                    className="form-label text-"
                  >
                    Search by JD ID:
                  </label>
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select select-box border-dark"
                      id="jobDescriptionId"
                      title="Search By JD ID"
                      aria-label="Job Description ID"
                      value={jobDescriptionId}
                      onChange={(e) => setJobDescriptionId(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a JD ID
                      </option>
                      {jobDescriptionIds.map((id, index) => (
                        <option key={index} value={id}>
                          {id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-lg-3 col-md-5 mb-1">
                  <label
                    htmlFor="candidateCurrentStatus"
                    className="form-label"
                  >
                    Search By Candidate Status:
                  </label>
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select select-box border-dark"
                      id="candidateCurrentStatus"
                      title="Search By Candidate Status"
                      aria-label="Candidate Current Status"
                      value={candidateCurrentStatus}
                      onChange={(e) =>
                        setCandidateCurrentStatus(e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select a Status
                      </option>
                      {candidateCurrentStatuses
                        .filter((status) => status) // Filters out empty or falsy values
                        .map((status, index) => (
                          <option key={index} value={status}>
                            {status}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="fromDate" className="form-label">
                    From Date:
                  </label>
                  <input
                    className="form-control border-dark bg-transparent"
                    type="date"
                    id="fromDate"
                    title="From Date"
                    aria-label="From Date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="toDate" className="form-label">
                    To Date:
                  </label>
                  <input
                    className="form-control border-dark bg-transparent"
                    type="date"
                    id="toDate"
                    title="To Date"
                    aria-label="To Date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-3 col-md-5 mb-1">
                  <label
                    htmlFor="validation"
                    className="form-label border-dark text-"
                  >
                    Search By Validation:
                  </label>
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select select-box border-dark"
                      id="validation"
                      title="Search By Validation"
                      aria-label="Validation"
                      value={validation}
                      onChange={(e) => setValidation(e.target.value)}
                    >
                      <option value="" disabled>
                        Select Yes or No
                      </option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-danger ms-1"
                      title="Clear All"
                      onClick={handleClearOptions}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                </div>

                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="minExperience" className="form-label">
                    Min Experience:
                  </label>
                  <input
                    type="text"
                    className="form-control border-dark bg-transparent"
                    id="minExperience"
                    placeholder="e.g. 3"
                    value={minExperience}
                    onChange={(e) => setMinExperience(e.target.value)}
                  />
                </div>

                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="maxExperience" className="form-label">
                    Max Experience:
                  </label>
                  <input
                    type="text"
                    className="form-control border-dark bg-transparent"
                    id="maxExperience"
                    placeholder="e.g. 7"
                    value={maxExperience}
                    onChange={(e) => setMaxExperience(e.target.value)}
                  />
                </div>

                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="skillsFilter" className="form-label">
                    Search By Skills:
                  </label>
                  <input
                    type="text"
                    className="form-control border-dark bg-transparent"
                    id="skillsFilter"
                    placeholder="e.g. React, Node"
                    value={skillsFilter}
                    onChange={(e) => setSkillsFilter(e.target.value)}
                  />
                </div>

                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="locationFilter" className="form-label">
                    Search By Location:
                  </label>
                  <input
                    type="text"
                    className="form-control border-dark bg-transparent"
                    id="locationFilter"
                    placeholder="e.g. Bangalore"
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                  />
                </div>
                <div className="col-lg-3 col-md-5 d-flex align-items-end justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-info mt-4 ms-3"
                    onClick={handleDownload}
                    title="Download Candidates Data"
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-info"
                    onClick={() => {
                      if (selectedCandidateIds.length === 0) {
                        alert("Please select at least one profile.");
                      } else {
                        setShowSendMailModal(true);
                      }
                    }}
                    title="Send Email to selected candidates"
                  >
                    Send Email
                  </button>
                </div>
              </form>
            </div>
            {/* Conditionally render the "Show All Data" button below the Download button */}
            {candidateStatusQuery && (
              <div className="text-end mt-2">
                <button
                  className="btn btn-outline-info ms-2"
                  onClick={handleShowAllData}
                >
                  Show All Data
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="table-responsive table-wrapper text-center">
            <table
              className="table table- table-hover table-bordered border-dark"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "800px",
              }}
            >
              <thead className="text-center tb_header border-dark">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="border-dark"
                      checked={
                        paginatedItems.length > 0 &&
                        paginatedItems.every((item) =>
                          selectedCandidateIds.includes(item.id)
                        )
                      }
                      onChange={handleSelectAllPage}
                    />
                  </th>
                  <th
                    style={{ width: "100px", cursor: "pointer" }}
                    onClick={() => requestSort("id")}
                  >
                    ID {getSortArrow("id")}
                  </th>
                  <th
                    style={{ width: "100px", cursor: "pointer" }}
                    onClick={() => requestSort("job_description_id")}
                  >
                    JD ID {getSortArrow("job_description_id")}
                  </th>
                  <th
                    style={{ width: "100px", cursor: "pointer" }}
                    onClick={() => requestSort("updated_at")}
                  >
                    Date {getSortArrow("updated_at")}
                  </th>

                  <th
                    style={{ width: "200px", cursor: "pointer" }}
                    onClick={() => requestSort("phone_number")}
                  >
                    Phone Number {getSortArrow("phone_number")}
                  </th>
                  <th
                    style={{ width: "300px", cursor: "pointer" }}
                    onClick={() => requestSort("email")}
                  >
                    Email {getSortArrow("email")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("experience")}
                  >
                    Experience {getSortArrow("experience")}
                  </th>
                  <th
                    style={{ width: "250px", cursor: "pointer" }}
                    onClick={() => requestSort("experience")}
                  >
                    Years of Experience {getSortArrow("experience")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("education")}
                  >
                    Education {getSortArrow("education")}
                  </th>
                  <th
                    style={{ width: "200px", cursor: "pointer" }}
                    onClick={() => requestSort("name")}
                  >
                    Name {getSortArrow("name")}
                  </th>
                  <th
                    style={{ width: "350px", cursor: "pointer" }}
                    onClick={() => requestSort("skills")}
                  >
                    Skills {getSortArrow("skills")}
                  </th>
                  <th
                    style={{ width: "150px", cursor: "pointer" }}
                    onClick={() => requestSort("validation")}
                  >
                    Validation {getSortArrow("validation")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("reason")}
                  >
                    Reason {getSortArrow("reason")}
                  </th>
                  <th style={{ width: "350px" }}>Rating</th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("file_name")}
                  >
                    File Name {getSortArrow("file_name")}
                  </th>
                  <th
                    style={{ width: "300px", cursor: "pointer" }}
                    onClick={() => requestSort("recruiter_feedback")}
                  >
                    Recruiter's&nbsp;Feedback
                    {getSortArrow("recruiter_feedback")}
                  </th>
                  <th
                    style={{ width: "200px" }}
                    onClick={() => requestSort("profile_age")}
                  >
                    Profile's Age {getSortArrow("profile_age")}
                  </th>
                  <th
                    style={{ width: "300px" }}
                    onClick={() => requestSort("candidate_current_status")}
                  >
                    Candidate Current Status{" "}
                    {getSortArrow("candidate_current_status")}
                  </th>
                  <th
                    style={{ width: "250px" }}
                    onClick={() => requestSort("current_organization")}
                  >
                    Current&nbsp;Organization{" "}
                    {getSortArrow("current_organization")}
                  </th>
                  <th
                    style={{ width: "200px" }}
                    onClick={() => requestSort("current_location")}
                  >
                    Current&nbsp;Location {getSortArrow("current_location")}
                  </th>
                  <th
                    style={{ width: "200px" }}
                    onClick={() => requestSort("location")}
                  >
                    Location {getSortArrow("location")}
                  </th>
                  <th
                    style={{ width: "200px" }}
                    onClick={() => requestSort("ctc")}
                  >
                    CTC {getSortArrow("ctc")}
                  </th>
                  <th
                    style={{ width: "200px" }}
                    onClick={() => requestSort("ectc")}
                  >
                    ECTC {getSortArrow("ectc")}
                  </th>
                  <th style={{ width: "200px" }}>Actions</th>
                </tr>
              </thead>
              <tbody className="text-center table-secondary border-dark">
                {paginatedItems.length > 0 ? (
                  paginatedItems.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          className="border-dark"
                          checked={selectedCandidateIds.includes(item.id)}
                          onChange={() => handleCheckboxChange(item.id)}
                        />
                      </td>
                      <td>{item.id}</td>
                      <td>{item.job_description_id}</td>

                      <td>
                        {new Date(item.updated_at).toISOString().slice(0, 10)}
                      </td>

                      <td>{item.phone_number}</td>
                      <td>{item.email}</td>
                      <td>{item.experience}</td>
                      <td>{item.exp_years}</td>
                      <td>{item.education}</td>
                      <td>{item.name}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.skills}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.validation}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.reason}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        <RatingBreakdown breakdown={item.rating_breakdown} />
                      </td>
                      {/* <td>{item.file_name}</td> */}
                      {/* <td>
                        {item.file_name && item.resume_url ? (
                          <a
                            href={item.resume_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#007BFF",
                              textDecoration: "underline",
                            }}
                          >
                            {item.file_name}
                          </a>
                        ) : (
                          item.file_name || "-"
                        )}
                      </td> */}
                      <td>
                        {item.file_name ? (
                          <a
                            href={
                              item.resume_url ||
                              `https://backend.theairecruiter.ai/media/resumes/${item.file_name}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#007BFF",
                              textDecoration: "underline",
                            }}
                          >
                            {item.file_name}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Dropdown for Recruiter's Feedback */}
                      <td>
                        <select
                          value={item.recruiter_feedback || ""}
                          onChange={(e) =>
                            handleChange(e, item.id, "recruiter_feedback")
                          }
                          className="form-select select-box bg-transparent"
                          style={{ border: "none" }}
                        >
                          <option value="">Select Feedback</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </td>
                      <td>{item.profile_age} (days)</td>

                      <td>
                        {/* {console.log(item.candidate_current_status)} */}
                        <select
                          value={item.candidate_current_status || ""}
                          onChange={(e) =>
                            handleChange(e, item.id, "candidate_current_status")
                          }
                          className="form-select select-box bg-transparent"
                          style={{ border: "none" }}
                        >
                          <option value="">
                            Select Candidate Current Status
                          </option>
                          {candidateCurrentStatusestb.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{item.current_organization}</td>
                      <td>{item.current_location}</td>
                      <td>{item.location}</td>
                      <td>{item.ctc}</td>
                      <td>{item.ectc}</td>
                      <td>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          type="button"
                          onClick={() => handleInitiateOnboarding(item.id)}
                        >
                          Initiate onboarding
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="30" className="text-start">
                      <h4>No data available</h4>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showSendMailModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div
                className="modal-header justify-content-header text-white font_header"
                style={{
                  background: "#008cba",
                }}
              >
                <h5 className="modal-title text-center w-100">Send Mail</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSendMailModal(false)}
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label
                      htmlFor="message-text"
                      className="col-form-label border-dark"
                    >
                      Message:
                    </label>
                    <textarea
                      className="form-control border-dark"
                      id="message-text"
                      rows="4"
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      placeholder="Enter message to include in email..."
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="jd-file"
                      className="col-form-label border-dark"
                    >
                      Attach JD (optional):
                    </label>
                    <input
                      type="file"
                      className="form-control border-dark"
                      id="jd-file"
                      onChange={(e) => setAttachedJDFile(e.target.files[0])}
                    />
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => setShowSendMailModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-outline-info"
                  onClick={handleSendFilteredResumes}
                >
                  Send Mail
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination code */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        paginate={(page) => setCurrentPage(page)}
      />
    </>
  );
}

export default AllData;
